<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { RouterLink } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { formatApiError } from '@yuksalish/api-client'
import { useAuthStore } from '@yuksalish/auth'
import { useOperatorApi } from '../composables/useOperatorApi'
import type { MachineStatus } from '../types/operator'

const { t } = useI18n()
const auth = useAuthStore()
const {
  listMachines,
  getEquipmentMachine,
  createEquipmentMachine,
  updateEquipmentMachine,
} = useOperatorApi()

const machines = ref<MachineStatus[]>([])
const loading = ref(true)
const error = ref('')
const statusFilter = ref('')
const message = ref('')

// --- Machine master form (reference_items type=equipment) ---
const canManageMachines = computed(() => auth.user?.permissions.includes('products.update') ?? false)
const showForm = ref(false)
const formLoading = ref(false)
const saving = ref(false)
const formError = ref('')
const editingId = ref<number | null>(null)
const form = reactive({
  code: '',
  name: '',
  sort_order: '',
  is_active: '1',
})

function openCreate(): void {
  editingId.value = null
  form.code = ''
  form.name = ''
  form.sort_order = ''
  form.is_active = '1'
  formError.value = ''
  showForm.value = true
}

async function openEdit(machine: MachineStatus): Promise<void> {
  editingId.value = machine.id
  form.code = ''
  form.name = machine.name
  form.sort_order = ''
  form.is_active = '1'
  formError.value = ''
  showForm.value = true
  // The list row only carries id/name — load the full master record.
  formLoading.value = true
  try {
    const record = await getEquipmentMachine(machine.id)
    form.code = record.code
    form.name = record.name
    form.sort_order = record.sort_order ? String(record.sort_order) : ''
    form.is_active = record.is_active ? '1' : '0'
  } catch (cause) {
    formError.value = formatApiError(cause)
  } finally {
    formLoading.value = false
  }
}

async function submitForm(): Promise<void> {
  formError.value = ''
  if (!form.code.trim() || !form.name.trim()) {
    formError.value = t('operator.machines.formRequired')
    return
  }
  saving.value = true
  try {
    const payload = {
      code: form.code.trim(),
      name: form.name.trim(),
      sort_order: form.sort_order.trim() ? Number(form.sort_order) : 0,
      is_active: Number(form.is_active),
    }
    if (editingId.value === null) {
      await createEquipmentMachine(payload)
      message.value = t('operator.machines.created')
    } else {
      await updateEquipmentMachine(editingId.value, payload)
      message.value = t('operator.machines.updated')
    }
    showForm.value = false
    await load()
  } catch (cause) {
    formError.value = formatApiError(cause)
  } finally {
    saving.value = false
  }
}

async function load() {
  loading.value = true
  error.value = ''
  try {
    const result = await listMachines({
      status: statusFilter.value || undefined,
      page: 1,
      per_page: 100,
    })
    machines.value = result.items
  } catch (cause) {
    error.value = formatApiError(cause)
  } finally {
    loading.value = false
  }
}

watch(statusFilter, load)
onMounted(load)

const filtered = computed(() => machines.value)
const runningCount = computed(() => machines.value.filter((m) => m.status === 'running').length)
const downCount = computed(() => machines.value.filter((m) => m.status === 'down').length)

function statusClass(status: string) {
  return {
    running: 'status-running',
    idle: 'status-idle',
    down: 'status-down',
    setup: 'status-setup',
  }[status] ?? ''
}

function statusLabel(status: string): string {
  if (status === 'running') return t('operator.machines.running')
  if (status === 'idle') return t('operator.machines.idle')
  if (status === 'down') return t('operator.machines.down')
  if (status === 'setup') return t('operator.machines.setup')
  return status
}
</script>

<template>
  <div class="page-heading">
    <div>
      <p class="eyebrow">{{ t('operator.machines.eyebrow') }}</p>
      <h2>{{ t('operator.machines.title') }}</h2>
      <p>{{ t('operator.machines.description') }}</p>
    </div>
    <button v-if="canManageMachines" class="btn-primary heading-action" type="button" @click="openCreate">
      {{ t('operator.machines.newMachine') }}
    </button>
  </div>

  <p v-if="message" class="action-message" role="status">{{ message }}</p>

  <!-- Summary + filter -->
  <div class="toolbar">
    <div class="summary" v-if="!loading && !error">
      <span>{{ t('operator.machines.total', { count: machines.length }) }}</span>
      <span class="dot-running">{{ t('operator.machines.runningCount', { count: runningCount }) }}</span>
      <span class="dot-down">{{ t('operator.machines.downCount', { count: downCount }) }}</span>
    </div>
    <select v-model="statusFilter" class="filter-select">
      <option value="">{{ t('operator.machines.allStatuses') }}</option>
      <option value="running">{{ t('operator.machines.running') }}</option>
      <option value="idle">{{ t('operator.machines.idle') }}</option>
      <option value="down">{{ t('operator.machines.down') }}</option>
      <option value="setup">{{ t('operator.machines.setup') }}</option>
    </select>
  </div>

  <!-- Loading -->
  <div v-if="loading" class="state-message"><p>{{ t('operator.machines.loading') }}</p></div>
  <!-- Error -->
  <div v-else-if="error" class="state-message error">
    <p>{{ error }}</p>
    <button class="btn-primary" @click="load">{{ t('operator.machines.retry') }}</button>
  </div>

  <!-- Empty -->
  <div v-else-if="filtered.length === 0" class="state-message">
    <p>{{ t('operator.machines.empty') }}</p>
  </div>

  <!-- Table -->
  <table v-else class="data-table">
    <thead>
      <tr>
        <th>{{ t('operator.machines.machine') }}</th>
        <th>{{ t('operator.machines.status') }}</th>
        <th>{{ t('operator.machines.order') }}</th>
        <th>{{ t('operator.machines.lot') }}</th>
        <th>{{ t('operator.machines.counter') }}</th>
        <th>{{ t('operator.machines.fault') }}</th>
        <th>{{ t('operator.machines.actions') }}</th>
      </tr>
    </thead>
    <tbody>
      <tr v-for="m in filtered" :key="m.id">
        <td class="cell-name">{{ m.name }}</td>
        <td>
          <span :class="['status-badge', statusClass(m.status)]">{{ statusLabel(m.status) }}</span>
        </td>
        <td class="cell-code">{{ m.current_order_code ?? '—' }}</td>
        <td>{{ m.current_lot_number ?? '—' }}</td>
        <td class="cell-qty">{{ m.counter_shift.toLocaleString() }}</td>
        <td>
          <span v-if="m.fault_active" class="fault-badge">{{ t('operator.machines.activeFault') }}</span>
          <span v-else class="no-fault">{{ t('operator.machines.noFault') }}</span>
        </td>
        <td class="cell-actions">
          <RouterLink :to="`/machines/${m.id}`" class="link-action">{{ t('operator.machines.open') }}</RouterLink>
          <button v-if="canManageMachines" class="link-action edit-action" type="button" @click="openEdit(m)">
            {{ t('operator.machines.edit') }}
          </button>
        </td>
      </tr>
    </tbody>
  </table>

  <!-- Machine create/edit modal -->
  <div v-if="showForm" class="modal-overlay" @click.self="showForm = false">
    <form class="modal" @submit.prevent="submitForm">
      <h3>{{ editingId === null ? t('operator.machines.createTitle') : t('operator.machines.editTitle') }}</h3>
      <label>{{ t('operator.machines.code') }}
        <input v-model="form.code" type="text" :disabled="formLoading" required />
      </label>
      <label>{{ t('operator.machines.name') }}
        <input v-model="form.name" type="text" :disabled="formLoading" required />
      </label>
      <label>{{ t('operator.machines.sortOrder') }}
        <input v-model="form.sort_order" type="number" min="0" step="1" :disabled="formLoading" />
      </label>
      <label>{{ t('operator.machines.status') }}
        <select v-model="form.is_active" :disabled="formLoading">
          <option value="1">{{ t('operator.machines.active') }}</option>
          <option value="0">{{ t('operator.machines.inactive') }}</option>
        </select>
      </label>
      <small v-if="formError" class="error">{{ formError }}</small>
      <div class="modal-actions">
        <button type="button" class="btn-secondary" @click="showForm = false">{{ t('operator.machines.cancel') }}</button>
        <button type="submit" class="btn-primary" :disabled="saving || formLoading">
          {{ saving ? t('operator.machines.saving') : t('operator.machines.save') }}
        </button>
      </div>
    </form>
  </div>
</template>

<style scoped>
.page-heading { margin-bottom: 20px; }
.eyebrow {
  margin: 0 0 4px;
  color: #65736e;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
}
h2 { margin: 0 0 4px; font-size: 22px; font-weight: 700; }
.page-heading p { margin: 0; color: #5f6b6a; font-size: 14px; }

.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
}
.summary { display: flex; gap: 16px; font-size: 13px; color: #65736e; }
.dot-running::before { content: '● '; color: #1f5c4b; }
.dot-down::before { content: '● '; color: #b33a3a; }
.filter-select {
  padding: 8px 14px;
  border: 1px solid #d4d8d5;
  border-radius: 8px;
  font-size: 13px;
  background: #fff;
  min-width: 160px;
}

.state-message { padding: 40px 0; text-align: center; color: #5f6b6a; }
.state-message.error { color: #b33a3a; }

.data-table {
  width: 100%;
  border-collapse: collapse;
  background: #fff;
  border: 1px solid #e2e6e3;
  border-radius: 10px;
  overflow: hidden;
}
.data-table th {
  text-align: left;
  padding: 12px 16px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  color: #65736e;
  background: #f6f8f7;
  border-bottom: 1px solid #e2e6e3;
}
.data-table td {
  padding: 12px 16px;
  font-size: 13px;
  color: #1a2b26;
  border-bottom: 1px solid #f0f2f1;
}
.data-table tr:last-child td { border-bottom: none; }
.cell-name { font-weight: 600; }
.cell-code { font-weight: 700; font-family: monospace; }
.cell-qty { font-weight: 700; }

.status-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  background: #eaf0ed;
  color: #3d5a4f;
}
.status-running { background: #dcefe7; color: #1f5c4b; }
.status-idle { background: #eaf0ed; color: #65736e; }
.status-down { background: #fde8e8; color: #b33a3a; }
.status-setup { background: #fef3cd; color: #856404; }

.fault-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 700;
  background: #fde8e8;
  color: #b33a3a;
}
.no-fault { color: #8b9a95; font-size: 12px; }

.link-action {
  color: #147a8a;
  font-weight: 600;
  font-size: 12px;
  text-decoration: none;
}
.link-action:hover { text-decoration: underline; }

.btn-primary {
  display: inline-block;
  margin-top: 12px;
  padding: 8px 20px;
  background: #147a8a;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}
.btn-primary:hover { background: #0f6573; }

.page-heading { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; }
.heading-action { margin-top: 0; }
.action-message { margin: 0 0 14px; padding: 9px 12px; border: 1px solid #b8ddce; border-radius: 8px; background: #f1faf6; color: #1f5c4b; font-size: 13px; font-weight: 700; }
.cell-actions { display: flex; align-items: center; gap: 12px; }
.edit-action { padding: 0; border: 0; background: none; cursor: pointer; }

.modal-overlay { position: fixed; z-index: 50; inset: 0; display: grid; padding: 20px; background: rgb(15 30 26 / 45%); place-items: center; }
.modal { display: grid; width: min(420px, 100%); gap: 12px; padding: 20px; border-radius: 12px; background: #fff; box-shadow: 0 20px 60px rgb(15 30 26 / 30%); }
.modal h3 { margin: 0; font-size: 16px; color: #1a2b26; }
.modal label { display: grid; gap: 6px; color: #53625d; font-size: 13px; font-weight: 700; }
.modal input, .modal select { padding: 9px 10px; border: 1px solid #cbd7d2; border-radius: 8px; font: inherit; }
.modal-actions { display: flex; justify-content: flex-end; gap: 8px; }
.modal .btn-secondary { padding: 8px 16px; border: 1px solid #cbd7d2; border-radius: 8px; background: #fff; color: #285b9a; font-weight: 700; cursor: pointer; }
.modal .btn-primary { margin-top: 0; }
.modal .error { color: #b33a3a; font-size: 12px; }
</style>
